import {
  createContext,
  useContext,
  useState,
  type FormEvent,
  type ReactNode,
} from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { ApiError } from "../api/client.ts";
import { fetchWhoami, login, type Identity } from "../api/auth.ts";

// Session gate for the whole app. whoami on mount: 401 -> login form,
// success -> identity context + children. Server-side middleware is the
// security boundary; this component is UX.

const TEXT = "#cfe0ff";
const MUTED = "#8a93a6";
const RED = "#e07070";
const ACCENT = "#6699ff";
const BORDER = "rgba(255,255,255,0.08)";
const CARD_BG = "rgba(255,255,255,0.03)";

const IdentityContext = createContext<Identity | null>(null);

export function useIdentity(): Identity {
  const id = useContext(IdentityContext);
  if (!id) throw new Error("useIdentity outside LoginGate");
  return id;
}

export function LoginGate({ children }: { children: ReactNode }) {
  const q = useQuery({
    queryKey: ["auth", "whoami"],
    queryFn: fetchWhoami,
    retry: false,
    staleTime: 5 * 60 * 1000,
  });

  if (q.isLoading) {
    return (
      <div style={{ color: MUTED, padding: 48, textAlign: "center", fontSize: 14 }}>
        Loading…
      </div>
    );
  }

  const unauthenticated =
    q.isError && q.error instanceof ApiError && q.error.status === 401;

  if (unauthenticated) return <LoginForm />;

  if (q.isError) {
    return (
      <div style={{ color: RED, padding: 48, textAlign: "center", fontSize: 14 }}>
        {q.error instanceof Error ? q.error.message : "Session check failed"}
      </div>
    );
  }

  return (
    <IdentityContext.Provider value={q.data!}>{children}</IdentityContext.Provider>
  );
}

// Landing page for a counterparty session: the selected side if the account has
// it, else the account's only side, else the portal dashboard.
function portalLanding(me: { sides?: string[] } | null, pick: "borrower" | "lender" | null): string {
  const sides = me?.sides ?? [];
  if (pick === "lender" && sides.includes("lender")) return "/portal/capital";
  if (pick === "borrower" && sides.includes("borrower")) return "/portal/financing";
  if (sides.length === 1) return sides[0] === "lender" ? "/portal/capital" : "/portal/financing";
  return "/portal";
}

function LoginForm() {
  const queryClient = useQueryClient();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [showPw, setShowPw] = useState(false);   // every password field gets a show/hide toggle (product convention)
  // Single front door: clients/operators sign in with a username; borrowers and
  // lenders (counterparty portal) with an email. The selection decides the auth
  // endpoint and the landing page.
  const [who, setWho] = useState<"client" | "borrower" | "lender">("client");
  useEffect(() => {
    // An already-signed-in borrower/lender opening the root goes straight to the portal.
    fetch("/api/v1/mm/auth/whoami", { credentials: "same-origin" })
      .then((r) => (r.ok ? r.json() : null))
      .then((me) => { if (me) window.location.assign(portalLanding(me, null)); })
      .catch(() => undefined);
  }, []);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setError(null);
    if (who === "client") {
      login(username, password)
        .then(() => queryClient.invalidateQueries({ queryKey: ["auth", "whoami"] }))
        .catch((err: unknown) => {
          setError(err instanceof Error ? err.message : "Login failed");
        })
        .finally(() => setBusy(false));
      return;
    }
    fetch("/api/v1/mm/auth/login", {
      method: "POST", credentials: "same-origin", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email: username, password }),
    })
      .then(async (r) => {
        const b = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(b.detail || `Login failed (${r.status})`);
        window.location.assign(portalLanding(b, who));
      })
      .catch((err: unknown) => {
        setError(err instanceof Error ? err.message : "Login failed");
        setBusy(false);
      });
  };

  const field = {
    width: "100%",
    padding: "9px 12px",
    fontSize: 14,
    color: TEXT,
    background: "rgba(255,255,255,0.05)",
    border: `1px solid rgba(255,255,255,0.14)`,
    borderRadius: 6,
    marginBottom: 12,
    boxSizing: "border-box",
  } as const;

  return (
    <div
      className="login-wrap"
      style={{
        minHeight: "70vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <form
        onSubmit={submit}
        className="login-card"
        style={{
          width: 340,
          maxWidth: "100%",
          background: CARD_BG,
          border: `1px solid ${BORDER}`,
          borderRadius: 10,
          padding: "28px 28px 22px",
        }}
      >
        <div style={{ fontSize: 18, fontWeight: 700, color: TEXT, marginBottom: 2 }}>
          LiquidTick
        </div>
        <div style={{ fontSize: 12, color: MUTED, marginBottom: 14 }}>
          {who === "client" ? "Liquidity governance platform" : "Counterparty network — borrowers and lenders"}
        </div>
        <div style={{ display: "flex", gap: 6, marginBottom: 14 }}>
          {(["client", "borrower", "lender"] as const).map((id) => (
            <button key={id} type="button" onClick={() => { setWho(id); setError(null); }}
              style={{ flex: 1, padding: "7px 0", fontSize: 12, cursor: "pointer", borderRadius: 6,
                border: `1px solid ${who === id ? ACCENT : "rgba(255,255,255,0.14)"}`,
                background: who === id ? "rgba(102,153,255,0.16)" : "transparent",
                color: who === id ? TEXT : MUTED }}>
              {id === "client" ? "Client" : id === "borrower" ? "Borrower" : "Lender"}
            </button>
          ))}
        </div>
        <input
          style={field}
          placeholder={who === "client" ? "Username" : "Email"}
          type={who === "client" ? "text" : "email"}
          value={username}
          autoComplete="username"
          autoFocus
          onChange={(e) => setUsername(e.target.value)}
        />
        <div style={{ position: "relative", marginBottom: 12 }}>
          <input
            style={{ ...field, marginBottom: 0, paddingRight: 60 }}
            placeholder="Password"
            type={showPw ? "text" : "password"}
            value={password}
            autoComplete="current-password"
            onChange={(e) => setPassword(e.target.value)}
          />
          <button type="button" onClick={() => setShowPw(!showPw)} aria-label={showPw ? "Hide password" : "Show password"}
            style={{ position: "absolute", right: 6, top: "50%", transform: "translateY(-50%)", fontSize: 12, color: ACCENT,
              background: "transparent", border: "none", cursor: "pointer", padding: "4px 6px" }}>
            {showPw ? "Hide" : "Show"}
          </button>
        </div>
        {error && (
          <div style={{ color: RED, fontSize: 12, marginBottom: 12 }}>{error}</div>
        )}
        <button
          type="submit"
          disabled={busy || !username || !password}
          style={{
            width: "100%",
            padding: "9px 0",
            fontSize: 14,
            fontWeight: 700,
            color: "#0a0e17",
            background: busy ? MUTED : ACCENT,
            border: "none",
            borderRadius: 6,
            cursor: busy ? "default" : "pointer",
          }}
        >
          {busy ? "Signing in…" : "Sign in"}
        </button>
        {who !== "client" && (
          <div style={{ fontSize: 12, color: MUTED, marginTop: 12, textAlign: "center", lineHeight: 1.9 }}>
            New {who} on LiquidTick? <a href="/portal/register" style={{ color: ACCENT }}>Create an account</a><br />
            <a href="/portal/forgot" style={{ color: ACCENT }}>Forgot your password?</a>
          </div>
        )}
      </form>
    </div>
  );
}
